import { ApiError, api } from "./client";
import type { PagedResult } from "@/types/api";
import type { BuyerLeadStatus } from "./buyers";
// Type-only, and deliberately the FUNNEL's own result shape rather than a staff
// copy of it. Assisted completion drives the same form through the same
// submission path, so a second declaration here could only ever drift — and the
// drift would show up as a broker's completed application rendering wrong.
import type { PrequalFinalizeResult } from "@/portal/api/prequalification";
// The ONE mirror of the C# `PrequalAnswerDisplayItem`, reused rather than
// re-declared: the staff drawer and the broker drawer render rows from the same
// server-side registry, and two TS shapes for one wire record is two places they
// can drift. Staff already imports a portal type in this file (above).
import type { PrequalAnswerDisplayItem } from "@/portal/api/prequalSite";
import type { PrequalInviteStatus } from "@/design-system/lib/prequalInviteStatus";

// Deal Flow → Prequalification admin surface (leads-template clone).
// Mirrors the C# records in Core/DTOs/Prequalification (camelCase wire).

export type PrequalificationRole = "Buyer" | "Seller";
export type PrequalificationOutcome = "Pass" | "SoftDecline";
export type PrequalificationDocType = "Letter" | "DealSheet" | "Analysis";

/**
 * Newest / oldest, matching the server's `PrequalListSort` tokens.
 *
 * Declared HERE rather than imported from the portal's identical union: a staff
 * module may never import from `@/portal/…`, and this is the staff side of the
 * same wire contract. The server reads it through `PrequalListSort.IsOldestFirst`,
 * so an absent, blank or unrecognized value is newest-first — a sort parameter
 * can never produce an error page or an empty list.
 *
 * Which COLUMN it orders is per-list and deliberately not stated here: invites
 * sort on `lastSentAtUtc`, drafts on `lastActivityAtUtc`. "Newest" means the most
 * recent thing that happened on that row, which is what a follow-up queue wants.
 */
export type PrequalSort = "newest" | "oldest";

/** Lifecycle statuses — string vocabulary (PrequalificationStatuses). */
export type PrequalificationStatus = "New" | "Reviewed" | "Converted" | "Disqualified";

/**
 * The FE mirror of `PrequalificationActivityType`. `ConvertedToPerson` keeps its
 * name for VALUE stability — it records a Party conversion now, and legacy rows
 * carry a person id.
 *
 * 🔴 This union had gone STALE BY TWO MEMBERS before anything pinned it: it
 * still announced "11 members" while the server had added
 * `DocumentGenerationFailed` and `DocumentsRegenerated`. Nothing broke, because
 * no client surface switches on this type today — which is precisely why it
 * drifted unnoticed, and why the first surface that DOES switch on it would have
 * silently mishandled two real row kinds. It is pinned to the C# enum now by
 * `prequalEnumUnionsMatchTheServer.test.ts`; do not restore a member count to
 * this comment, since the count is a fact about the source and this is a
 * comment.
 */
/** Mirror of the C# `PrequalificationCompletionMode` as a SUBMISSION records
 *  it (int-stored, sent by name) — every member, unlike the assisted-finalize
 *  body's `PrequalificationCompletionMode`, which accepts the two same-sitting
 *  values only. */
export type PrequalSubmissionCompletionMode =
  | "RemoteSelfServe" | "SameSittingClientDevice" | "SameSittingBrokerCertified"
  | "StaffCertifiedByPhone";

export type PrequalificationActivityType =
  | "Created" | "StatusChanged" | "Assigned" | "Archived" | "Unarchived"
  | "ConvertedToPerson" | "DocumentGenerated" | "Note"
  | "Emailed" | "EmailFailed" | "CameFromInvite"
  | "DocumentGenerationFailed" | "DocumentsRegenerated"
  | "InternalRelationshipVerificationChanged"
  | "SentToBuyerCrm" | "LinkedToBuyer" | "ClientEmailWithheld";

/**
 * Which package a BUYER asked for. Mirrors the server enum
 * `PrequalBuyerDocumentPath`, which is string-stored, so these are its member
 * names verbatim.
 *
 * Declared here rather than imported from the portal funnel's own copy
 * (`portal/api/prequalification.ts`): the staff and portal surfaces are kept
 * apart deliberately, and a short union is a cheaper duplication than the
 * coupling. Both mirror the SERVER, which is the thing that cannot drift.
 *
 * 🔴 That last sentence was an aspiration until #1230 (stamped prequal-v4):
 * NOTHING pinned either copy against the C# enum, so a member added on one side only would
 * have shipped silently — a staff filter quietly unable to express a path the
 * funnel was already taking. `prequalBuyerDocumentPathUnions.test.ts` reads the
 * enum out of the C# source and pins both copies to it.
 */
export type PrequalBuyerDocumentPath = "LetterOnly" | "LetterAndDealSheet" | "InternalBuyer";

/**
 * Whether staff have checked the relationship an internal buyer asserted.
 * Mirrors the server enum `PrequalInternalVerification`, string-stored, so these
 * are its member names verbatim — and pinned to the C# source by
 * `prequalEnumUnionsMatchTheServer.test.ts` like its two siblings.
 *
 * 🔴 **IT GATES NOTHING (D6).** No document, no email and no later step reads
 * it; `NotVerified` blocks nothing anywhere in the product. A reader looking for
 * the place it stops a deal will not find one, and that is the design.
 *
 * 🔴 **NEVER RENDER IT WITHOUT THE PATH.** The column is NOT NULL on every
 * submission and defaults to `Unverified`, so every seller and every
 * capital-sized buyer sits there for life. A badge keyed on this value alone
 * would put an unverified marker on almost every row in the book — see
 * `internalVerificationBadge`.
 */
export type PrequalInternalVerification = "Unverified" | "Verified" | "NotVerified";

/**
 * The seller funnel's Step 1 · Path (Owner Recapitalization Loan, spec pack
 * U5a/U5b). Mirrors the server enum `PrequalSellerPath`, string-stored — pinned to
 * the C# source by `prequalEnumUnionsMatchTheServer.test.ts`. Null/absent on a
 * buyer and on a seller row written before the fork, which reads as a sale.
 */
export type PrequalSellerPath = "Sale" | "OwnerRecapitalization";

export interface PrequalificationSummaryDto {
  id: string;
  role: PrequalificationRole;
  firstName: string;
  lastName: string;
  email: string;
  industryOrBusiness?: string | null;
  outcome: PrequalificationOutcome;
  qualifiedAmount?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  hasLetter: boolean;
  hasDealSheet: boolean;
  /** The private analysis document. It IS produced — the enum comment calling
   *  it "not produced in v1" is stale — so the list column under-reported it
   *  until this field existed. */
  hasAnalysis: boolean;
  /** Which package this buyer asked for; absent when they were never asked — a
   *  seller, or any buyer from before the fork shipped.
   *
   *  🔴 It is what makes `hasDealSheet` readable. A letter-only buyer reads
   *  `false` there, and without this a reader cannot tell a document that
   *  failed to generate from one nobody ever asked for. */
  buyerDocumentPath?: PrequalBuyerDocumentPath | null;
  /** Whether staff have checked the asserted relationship. Read it WITH
   *  `buyerDocumentPath` and never alone — see the type's own note. */
  internalRelationshipVerification: PrequalInternalVerification;
  /** Whether a buyer in the Buyer CRM is linked to this completion — the
   *  list's "In Buyer CRM" column. Computed server-side by the one Z1 rule the
   *  detail page's button and the send endpoint also read. Meaningful for
   *  buyer rows only: render a dash for a seller. Absent from a server that
   *  predates the column. */
  inBuyerCrm?: boolean;
  /** The engine stamp the row was sized at. From `prequal-v7` a seller's and an
   *  internal buyer's `supportablePriceLow` / `supportablePriceHigh` are the
   *  10-year and 20-year prices (ruling Q6). */
  engineVersion?: string | null;
  /** prequal-v7 seller and internal-buyer passes: the Maximum Financed Amount on
   *  each schedule — AMOUNTS FINANCED, never prices — which the list column states
   *  (M42, ruling Q9). Absent on every other row. */
  maxFinanced20?: number | null;
  maxFinanced10?: number | null;
  /** The 10-year Maximum is under the program minimum amount financed. */
  belowMinimum10?: boolean | null;
  /** The seller's recorded Step 1 · Path (U5a): the "Recapitalization" badge
   *  renders on `OwnerRecapitalization` alone. Absent on a buyer and on a
   *  pre-fork seller (a sale). */
  sellerPath?: PrequalSellerPath | null;
  status: PrequalificationStatus;
  assignedToUserId?: string | null;
  isArchived: boolean;
  createdAt: string;
  /** Human-quotable identifier. On the wire since the reference-number work
   *  shipped; the list never carried it, so staff could not search or cite it. */
  referenceNumber?: string | null;
  /** White-label attribution — null for the Phoenix-native funnel. */
  partnerSlug?: string | null;
}

export interface PrequalificationStatsDto {
  total: number;
  byStatus: Record<PrequalificationStatus, number>;
  passCount: number;
  softDeclineCount: number;
}

/** Where a regeneration's FIGURES came from — never what was rendered. */
export type PrequalificationFigureSource =
  | "DocumentSnapshot"
  | "SubmissionSnapshot"
  | "Recomputed";

export interface PrequalificationTermDivergenceDto {
  name: string;
  then: number;
  now: number;
}

export interface PrequalificationRegenerationResultDto {
  documents: PrequalificationDocumentDto[];
  produced: PrequalificationDocType[];
  failed: PrequalificationDocType[];
  figureSource: PrequalificationFigureSource;
  /** Program terms that have MOVED since the replayed snapshot was written.
   *  Surfaced, never acted on: the regenerated documents carry the ORIGINAL
   *  figures, because re-running the engine at today's terms would silently
   *  change a number the applicant may already be holding a letter about. */
  termDivergences: PrequalificationTermDivergenceDto[];
}

/** What a resend WOULD send, composed by the same code path the send runs.
 *  `blockedReason` non-null means the send is refused and says why — the
 *  subject and body may then be empty, which is the case where composing at all
 *  would put the wrong words on screen (a soft decline has no completion
 *  email). */
export interface PrequalificationResendDraftDto {
  toEmail: string;
  toName: string | null;
  subject: string;
  body: string;
  attachmentNames: string[];
  blockedReason: string | null;
}

export interface PrequalificationResendResultDto {
  sentAtUtc: string;
  attachmentNames: string[];
}

export interface PrequalificationDocumentDto {
  id: string;
  docType: PrequalificationDocType;
  generatedAtUtc: string;
  /** Replaced by a newer document of the same kind — i.e. a regeneration ran
   *  after it. The staff detail lists superseded documents DELIBERATELY: a
   *  regenerate is append-only because the old document is the record of what
   *  the applicant was actually sent, so hiding it would hide the evidence.
   *  What staff need is to tell them apart. The applicant's own result screen
   *  is the surface that narrows to one card per kind. */
  isSuperseded?: boolean;
  /** The exact HTML this document's PDF was rendered from is stored, so an
   *  editable Word copy can be downloaded (`documentWord`). False for every
   *  document generated before the HTML was stored. Staff-only. */
  hasWordCopy?: boolean;
}

export interface PrequalificationDocumentLinkDto {
  documentId: string;
  docType: PrequalificationDocType;
  url: string;
  expiresAtUtc: string;
}

export interface PrequalificationActivityDto {
  id: string;
  activityType: PrequalificationActivityType;
  description: string;
  metadataJson?: string | null;
  performedByUserId: string;
  timestamp: string;
}

export interface PrequalificationDetailDto {
  id: string;
  role: PrequalificationRole;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  answersJson: string;
  engineInputsJson?: string | null;
  engineVersion: string;
  outcome: PrequalificationOutcome;
  qualifiedAmount?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  knockoutReasonsJson?: string | null;
  unsureFlagsJson?: string | null;
  readinessFlagsJson?: string | null;
  status: PrequalificationStatus;
  assignedToUserId?: string | null;
  convertedToPersonId?: string | null;
  /** The Party this submission converted into — the pointer that makes a
   *  conversion reachable from the Borrowers / Sellers registries.
   *
   *  Rides ALONGSIDE `convertedToPersonId`, never instead of it: the two point
   *  at different tables and therefore at different deep links, and legacy
   *  conversions keep the older pointer. A converted submission has exactly one
   *  of them set, so both have to be read. */
  convertedToPartyId?: string | null;
  /** Which package this buyer asked for; see the summary type's member of the
   *  same name. */
  buyerDocumentPath?: PrequalBuyerDocumentPath | null;
  /** Whether staff have checked the asserted relationship; see the summary
   *  type's member of the same name. */
  internalRelationshipVerification: PrequalInternalVerification;
  /** When it was last set, or absent while nobody has looked. The two agree by
   *  construction — the server clears this whenever it writes `Unverified`. */
  internalRelationshipVerifiedAtUtc?: string | null;
  /** Who last set it. A plain identity id, resolved the same way every other
   *  actor id on this surface is. */
  internalRelationshipVerifiedByUserId?: string | null;
  /** How it was completed. `StaffCertifiedByPhone` shows "Completed by phone —
   *  certified by {name}, {date}"; `certifiedAtUtc` is an INSTANT. */
  completionMode?: PrequalSubmissionCompletionMode | null;
  certifiedByName?: string | null;
  certifiedAtUtc?: string | null;
  convertedAtUtc?: string | null;
  isArchived: boolean;
  consentAcceptedAtUtc: string;
  purgeEligibleAtUtc?: string | null;
  createdAt: string;
  documents: PrequalificationDocumentDto[];
  /** Human-quotable identifier. Populated on the server since it shipped; this
   *  interface simply omitted it, so the only reference number the detail page
   *  could show was the PREVIOUS submission's, on the chain link. */
  referenceNumber?: string | null;
  /** How long the stated figures stand. Dropped by this interface the same way
   *  `referenceNumber` was. */
  validThroughUtc?: string | null;
  /** The buyer's supportable band, recomputed at read time from the stored
   *  answers and engine version — deliberately NOT persisted columns. Null for
   *  sellers, who carry `supportablePriceLow`/`High` instead. */
  buyingPowerLow?: number | null;
  buyingPowerHigh?: number | null;
  /** White-label attribution — the partner slug this submission was sourced
   *  through, or null for the native funnel. Reporting only. */
  partnerSlug?: string | null;
  /** The buyer's PREVIOUS prequalification on this same site, when there is
   *  one. Both submissions stay live — this is a history link, not a
   *  supersession — and the earlier outcome rides it so the reader can see
   *  whether it matters without opening it. */
  previousSubmission?: PrequalificationChainLinkDto | null;
  /** prequal-v7 (1 November 2026 terms): the two payment schedules, 20-year
   *  first. Absent on a row sized before v7, a decline, or a result with no free
   *  cash flow to size (the capital letter path) — the page then reads as before
   *  (D15). Mirror of `PrequalScheduleFiguresDto`. */
  schedules?: PrequalScheduleFiguresDto[] | null;
  /** The seller's recorded Step 1 · Path (U5b). Absent on a buyer and on a
   *  pre-fork seller (a sale). */
  sellerPath?: PrequalSellerPath | null;
}

/** One payment schedule of a v7 result (§2 T5–T7), for STAFF views (D2): the
 *  Origination Coverage Ratio and the two flags are staff-only. Mirror of the
 *  C# `PrequalScheduleFiguresDto`. Money to the cent; ratios to four places. */
export interface PrequalScheduleFiguresDto {
  /** 240 (20-year) or 120 (10-year). */
  amortizationMonths: number;
  /** The Maximum Financed Amount — an AMOUNT FINANCED, never a price. */
  maximumFinancedAmount: number;
  /** "FiveTimesCashFlow" | "CoverageOnBuyerNotePrincipal" | "ProgramMaximum". */
  bindingLimit?: string | null;
  /** The Maximum plus the stated down payment. */
  purchaseFigure?: number | null;
  buyerNotePrincipal?: number | null;
  sizingCoverage?: number | null;
  originationCoverage?: number | null;
  belowMinimum: boolean;
  /** The named deal's price less the stated down payment, when a price was named. */
  statedAmountFinanced?: number | null;
  statedWithinMaximum?: boolean | null;
  statedOriginationCoverage?: number | null;
  /** D2 staff flag: the stated amount financed exceeds this schedule's Maximum. */
  statedExceedsMaximumFlag: boolean;
  /** D2 staff flag: an Origination Coverage Ratio here is under the 1.35x floor. */
  originationBelowFloorFlag: boolean;
  twelveMonthPrincipalAndInterest?: number | null;
  twelveMonthPaymentAdministrationFee?: number | null;
  /** The seller's Underwriting Fee at this schedule's Maximum (2.95% of the
   *  amount financed) — U5b's "Underwriting Fee at the maximum" row. */
  sellerUnderwritingFee?: number | null;
}

/** One step back along the re-prequalification chain. */
export interface PrequalificationChainLinkDto {
  id: string;
  outcome: PrequalificationOutcome;
  createdAt: string;
  referenceNumber?: string | null;
}

export interface PrequalificationListQuery {
  role?: PrequalificationRole;
  status?: PrequalificationStatus;
  outcome?: PrequalificationOutcome;
  assignedToUserId?: string;
  /** Narrow to submissions from the Phoenix-native funnel (no partner brand).
   *  A separate flag because `partnerBrandId` is a `Guid?` server-side, where
   *  null already means "unfiltered" — the two intents are not expressible in
   *  one field. */
  nativeOnly?: boolean;
  from?: string;
  to?: string;
  q?: string;
  /** When true, return ONLY archived submissions (the "Archived" view).
   *  Omitted/false excludes archived (the default — leads convention). */
  archived?: boolean;
  hasDealSheet?: boolean;
  /** Narrow to submissions that SHOULD have documents and do not. The server
   *  predicate is the authority (`PrequalificationAdminService`); this is a
   *  mirror, and it has now been stale TWICE, so read it as a summary and not
   *  as a specification.
   *
   *  THREE dimensions, not one:
   *   - outcome — a `Pass` owes the letter and the deal sheet; a `SoftDecline`
   *     owes the letter and the analysis, because there is no deal to price and
   *     the analysis is the only place that applicant is told what would need to
   *     change;
   *   - package — a buyer who asked for the letter ALONE owes no deal sheet.
   *     NULL reads as "wanted both", which is what keeps every submission taken
   *     before the fork in the filter;
   *   - presence — the document rows themselves.
   *
   *  The Analysis is deliberately NOT part of the PASS test — it is per-portal
   *  toggle-gated there, so its absence is correct for every row on a portal
   *  with the toggle off. Nor is the Program Brochure, which is not a generated
   *  document at all.
   *
   *  Distinct from `hasDealSheet`, which is the bare "no deal sheet" test and
   *  has never had a client sending it: that one is dominated by soft declines,
   *  which correctly produce nothing. */
  documentsMissing?: boolean;
  /** Narrow to internal buyers in one verification state — the queue a staff
   *  member works ("who has nobody looked at yet").
   *
   *  🔴 The server ANDs the internal path onto this, and has to: the column is
   *  on every submission, so `Unverified` alone would return nearly the whole
   *  book under a filter whose name promises internal buyers. */
  internalVerification?: PrequalInternalVerification;
  /** Narrow to completions that are (`true`) or are not (`false`) in the Buyer
   *  CRM. The server scopes `false` to buyer completions — a seller is never
   *  sent there. */
  inBuyerCrm?: boolean;
  /** U5a: narrow to sellers on one Step 1 · Path, server-side. `Sale` includes
   *  every pre-fork seller (no path recorded); either value limits the list to
   *  sellers. */
  sellerPath?: PrequalSellerPath;
  /** Narrow to submissions sourced through one partner site. Admin-only in
   *  practice — the brand list it is chosen from is gated on
   *  Partners.ManageBrands. */
  partnerBrandId?: string;
  page?: number;
  pageSize?: number;
}

export interface ConvertPrequalificationPayload {
  /** Link this existing party instead of creating one. Validated server-side
   *  against the party type DERIVED from the submission's role, so a wrong-type
   *  id and an unknown id are the same 404 — the picker is scoped to the derived
   *  type to make cross-type linking unreachable from the UI. */
  existingPartyId?: string | null;
  /** Correct the address the NEW PARTY is created with. Absent/blank = use the
   *  submission's own email. Ignored in link-existing mode.
   *
   *  It never touches the submission row: that is the audited record of what the
   *  applicant typed. */
  email?: string | null;
  overrideReason?: string | null;
}

// ── Tracked invites ─────────────────────────────────────────────────────────
// Mirrors Core/DTOs/Prequalification/PrequalInviteDtos.cs.

/** The EFFECTIVE status — persisted lifecycle with expiry folded in.
 *
 *  Re-exported from the shared vocabulary rather than declared here: the portal
 *  cannot import this module, so a second hand-maintained mirror lived in
 *  `portal/api/prequalSite.ts` and had drifted to seven members. One union now
 *  backs all three invite surfaces. Imported as well as re-exported because a
 *  bare `export type { … } from` creates no local binding, and the DTOs below
 *  reference the name. */
export type { PrequalInviteStatus };

export interface PrequalInviteDto {
  id: string;
  role: PrequalificationRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerBrandId?: string | null;
  partnerSlug?: string | null;
  status: PrequalInviteStatus;
  sentAtUtc: string;
  lastSentAtUtc: string;
  sendCount: number;
  expiresAtUtc: string;
  openedAtUtc?: string | null;
  completedAtUtc?: string | null;
  submissionId?: string | null;
  invitedByLabel?: string | null;
  /** The full link, so a sender can copy it out and deliver it themselves. */
  inviteUrl: string;
  invitedByUserId?: string | null;
  invitedByPortalUserId?: string | null;

  /** When the recipient first began answering behind a server-side draft.
   *
   *  The status could always report `Started`; the timestamp was simply not on
   *  the wire, so "opened three weeks ago and started yesterday" was
   *  unanswerable from the DTO. Nothing new is instrumented — this reads the
   *  column `PrequalificationDraftService` has always stamped. */
  startedAtUtc?: string | null;

  /** What the invitation was sent FROM — a source-entity token and its id, or
   *  nulls for one sent from the standalone surface. An invite carrying a
   *  submission is what binds the resulting attempt to that submission's
   *  listing automatically. */
  sourceEntityType?: string | null;
  sourceEntityId?: string | null;
}

/**
 * The site choice is deliberately two fields, not one nullable one: a bare
 * null `partnerBrandId` is ambiguous between "the Phoenix-native site" and
 * "nobody picked". The server rejects a request that sets neither, so the UI
 * must make the site an explicit decision rather than a default.
 */
export interface SendPrequalInvitePayload {
  role: PrequalificationRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerBrandId?: string | null;
  phoenixNative?: boolean;
  /** Acknowledgement that someone already has a live invite out to this buyer
   *  on this site. Omitted (so, false) on the first attempt, which is what
   *  makes the server answer 409 with the warning; the confirm step re-submits
   *  with true. An override, not a suppression — the server audits it. */
  confirmDuplicate?: boolean;
}

/** One live invite that already exists for the buyer being invited. Carries the
 *  sender's LABEL rather than an id: naming the person is the whole job, and
 *  the label is already denormalized onto the invite row. */
export interface PrequalInviteDuplicateDto {
  inviteId: string;
  sentByLabel: string;
  sentAtUtc: string;
  lastSentAtUtc: string;
  expiresAtUtc: string;
  status: string;
}

/** The 409 body behind a duplicate-invite warning. `message` is composed
 *  SERVER-side and must be rendered verbatim — composing a second one on the
 *  client is how the warning before the click and the error on a refused retry
 *  drift apart. */
export interface PrequalInviteDuplicateConflict {
  email: string;
  partnerBrandId?: string | null;
  message: string;
  existing: PrequalInviteDuplicateDto[];
}

/**
 * Recognise a duplicate-invite 409 and hand back its payload, or null for
 * anything else.
 *
 * Reads `details`, not `data`: the shared client drops the envelope's `data` on
 * a non-2xx, so `details` is the only structured channel that survives — the
 * counterparty and vendor create precedent.
 *
 * Status-checked as well as shape-checked. A 429 daily-cap refusal also carries
 * a `details` object, and matching on shape alone would eventually route one of
 * them into the wrong dialog.
 */
export function asDuplicateConflict(err: unknown): PrequalInviteDuplicateConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const details = err.details as Partial<PrequalInviteDuplicateConflict> | undefined;
  if (!details || typeof details.message !== "string" || !Array.isArray(details.existing)) {
    return null;
  }
  return details as PrequalInviteDuplicateConflict;
}

export interface PrequalInviteListQuery {
  status?: PrequalInviteStatus;
  /** Only invites the CALLER sent — the caller is resolved server-side. */
  mineOnly?: boolean;
  /** Only invites still live and uncompleted — the "waiting on someone" set. */
  outstanding?: boolean;
  /** Narrow to invites naming ONE partner site. Staff convenience, not
   *  isolation — the staff list is unscoped by construction and the
   *  partner-facing read resolves its brand from the session. */
  partnerBrandId?: string;
  /** Free-text over the invitee's first name, last name and email — those three
   *  and nothing else. An invite has no reference number to match, so there is
   *  no fourth arm. Blank or absent applies no predicate. */
  q?: string;
  /** `"newest"` | `"oldest"`, ordering on `lastSentAtUtc`. */
  sort?: PrequalSort;
  page?: number;
  pageSize?: number;
}

// ── In-progress drafts ──────────────────────────────────────────────────────
// Mirrors Core/DTOs/Prequalification/PrequalificationDraftDtos.cs (the shared,
// answer-free projection) and PrequalificationDraftAdminDtos.cs (the query).
//
// A PARALLEL source, not a filter on the submissions list: a draft is neither a
// submission nor an invite, so it has its own endpoint and its own section —
// exactly as the tracked invites do.

/**
 * One in-progress application, as a third party sees it. There is no answer
 * content on this shape and no field that could hold any: a follow-up caller
 * needs who, how far and how long ago, not the applicant's finances.
 */
export interface PrequalificationDraftSummaryDto {
  draftId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: PrequalificationRole;
  /**
   * NULL for an ORGANIC draft — somebody who arrived on the public funnel and
   * was never invited by anyone. Render the null as "not invited", never
   * through the invite-status badge map: reporting that Phoenix invited a
   * stranger who wandered in is worse than showing nothing.
   */
  inviteStatus?: PrequalInviteStatus | null;
  createdAt: string;
  lastActivityAtUtc: string;
  /** Numerator. `stepCount` is the funnel's own length (6 buyer / 7 seller). */
  stepIndex: number;
  stepCount: number;
  /** Display classification from the server's shared abandonment threshold —
   *  never recomputed here, or this list and the portal tab would disagree. */
  isAbandoned: boolean;
}

/** `All` INCLUDES the abandoned ones (the flag is a row state); `Abandoned`
 *  narrows to the follow-up queue, server-side. */
export type PrequalificationDraftScope = "All" | "Abandoned";

export interface PrequalificationDraftListQuery {
  scope?: PrequalificationDraftScope;
  /** Narrow to drafts started on ONE partner site — what makes the per-partner
   *  section on the admin Prequalification tab possible without a second read
   *  model. STAFF surfaces only: an organic draft has no sender but does carry a
   *  brand, so this returns rows nobody invited, which is correct for staff and
   *  would be a leak in a partner-facing query. */
  partnerBrandId?: string;
  /** Free-text over the applicant's first name, last name and email. Identity
   *  fields only — `PrequalificationDraftSummaryDto` has nowhere to put an
   *  answer, so searching one would be searching content this surface is not
   *  allowed to show. No reference-number arm either: a draft has none, because
   *  one is minted at finalize, which is the moment the draft stops existing. */
  q?: string;
  /** `"newest"` | `"oldest"`, ordering on `lastActivityAtUtc`. */
  sort?: PrequalSort;
  page?: number;
  pageSize?: number;
}

// ── Assisted completion ─────────────────────────────────────────────────────
// A staff member drives somebody else's form, in the same sitting. Mirrors
// Core/DTOs/Prequalification/PrequalificationAssistedDtos.cs and the shared
// wire shapes in Api/Controllers/PrequalificationAssistedContracts.cs.
//
// Every one of these can answer 403 WITH AN EMPTY BODY, and that single answer
// covers both "you may not" and "no such draft" — deliberately, because a
// 404/403 split over an id-addressed endpoint is an existence oracle. Treat it
// as one state.

/** Base64 on the wire — a `byte[]` server-side. Echoed back UNMODIFIED on the
 *  next write; a missing or stale stamp is a 409, never last-write-wins. */
export type PrequalRowVersion = string;

/** What an IN-OFFICE start returns. No token in any field: the caller is not
 *  the visitor — they drive the form themselves and address the draft by id
 *  from here on. */
export interface PrequalificationInOfficeStartDto {
  draftId: string;
  inviteId: string;
  partnerBrandId?: string | null;
  partnerSlug?: string | null;
  stepIndex: number;
  stepCount: number;
  rowVersion: PrequalRowVersion | null;
}

/** The draft as the assisting human loads it: the same answers the applicant
 *  would see, plus enough context to know what they are looking at.
 *
 *  **No resume token, in any field.** That token authorizes every anonymous
 *  read and write of the row, so surfacing it into a staff UI would turn a
 *  screen-share or a support ticket into a permanent credential leak. */
export interface PrequalificationDraftAssistedLoadDto {
  draftId: string;
  role: PrequalificationRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerBrandId?: string | null;
  partnerSlug?: string | null;
  originatingInviteId?: string | null;
  inviteStatus?: PrequalInviteStatus | null;
  stepIndex: number;
  /** The server's own denominator for this funnel — never assume 6 or 7. */
  stepCount: number;
  answersJson: string;
  rowVersion: PrequalRowVersion | null;
  createdAt: string;
  lastActivityAtUtc: string;
  isAbandoned: boolean;
}

export interface PrequalificationAssistedStepBody {
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  answersJson: string;
}

export interface PrequalificationDraftPersistResult {
  draftId: string | null;
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  error: string | null;
}

/**
 * How this application was completed. Only the two SAME-SITTING values are
 * accepted here; the server refuses anything else (including an omitted value)
 * as a renderable validation error rather than believing it.
 */
export type PrequalificationCompletionMode =
  | "SameSittingClientDevice"
  | "SameSittingBrokerCertified";

export interface PrequalificationAssistedFinalizeBody {
  rowVersion: PrequalRowVersion | null;
  consentAccepted: boolean;
  answersJson: string;
  phone?: string;
  /** Honeypot — hidden on the real form; always empty for humans. */
  website?: string;
  completionMode: PrequalificationCompletionMode;
  /** **Polarity.** The checkbox reads "email a copy to the client" and is
   *  defaulted ON, so UNCHECKING it sends `true` here. Omitting the field
   *  SENDS the documents. */
  suppressClientCopy: boolean;
}

// ── Send to the assignment queue (Buyer CRM) ─────────────────────────────

/** Who a linked completion belongs to. `buyerLeadId` and `name` are null when
 *  the caller lacks Buyers.Read: show "In Buyer CRM" with no name and no link. */
export interface PrequalificationLinkedBuyerDto {
  buyerLeadId?: string | null;
  name?: string | null;
  isArchived: boolean;
}

/** One existing buyer the completion might be. */
export interface PrequalificationBuyerCrmMatchDto {
  buyerLeadId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  /** "email" or "phone". */
  matchedOn: string;
  status: BuyerLeadStatus;
  isArchived: boolean;
  assignedSpecialistName?: string | null;
  /** False for an archived buyer; the reason is in `linkBlockedReason`. */
  canLink: boolean;
  linkBlockedReason?: string | null;
  /** The reference of the prequalification this buyer's packages already use,
   *  when it is a DIFFERENT completion: linking records this one and leaves
   *  that pointer where it is. */
  existingPrequalificationReference?: string | null;
  /** This buyer's primary prequalification already IS this completion. */
  alreadyPrimary: boolean;
}

/** Everything the send dialog needs. The SERVER decides every gate here —
 *  render what it says rather than re-deriving any of it. */
export interface PrequalificationBuyerCrmStateDto {
  submissionId: string;
  referenceNumber?: string | null;
  role: PrequalificationRole;
  eligible: boolean;
  ineligibleReason?: string | null;
  /** Prequalification.Read AND Buyers.Create. */
  canSend: boolean;
  linkedBuyer?: PrequalificationLinkedBuyerDto | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  outcome: PrequalificationOutcome;
  /** "Came from: site (owner)", composed server-side. */
  cameFrom: string;
  /** Set when the Buyer CRM will refuse a contact detail; the send is blocked. */
  contactProblem?: string | null;
  /** The phone could not be verified and goes to the notes. Advisory. */
  phoneNote?: string | null;
  matches: PrequalificationBuyerCrmMatchDto[];
}

export interface SendPrequalificationToBuyerCrmPayload {
  createAnyway?: boolean;
  linkToBuyerLeadId?: string | null;
}

export interface PrequalificationBuyerCrmResultDto {
  /** "created" | "linked" | "recorded" | "already". */
  outcome: "created" | "linked" | "recorded" | "already";
  buyerLeadId: string;
  buyerName: string;
  buyerTeamId?: string | null;
  buyerTeamName?: string | null;
  existingPrequalificationReference?: string | null;
}

/** `ApiError.details.code` on the 409 a send returns when the completion turned
 *  out to be in the Buyer CRM already. Mirrors
 *  `PrequalificationAlreadyInBuyerCrmException.Code`. */
export const PREQUAL_ALREADY_IN_BUYER_CRM_CODE = "prequalAlreadyInBuyerCrm";

/** The buyer named by that 409, or null when the error is anything else.
 *  Branches on the CODE, never on the message. */
export function alreadyInBuyerCrm(e: unknown): PrequalificationLinkedBuyerDto | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const details = e.details as
    | { code?: unknown; linked?: { buyerLeadId?: string | null; buyerName?: string | null; isArchived?: boolean } | null }
    | null
    | undefined;
  if (details?.code !== PREQUAL_ALREADY_IN_BUYER_CRM_CODE) return null;
  return {
    buyerLeadId: details.linked?.buyerLeadId ?? null,
    name: details.linked?.buyerName ?? null,
    isArchived: details.linked?.isArchived ?? false,
  };
}

const BASE = "/deal-flow/prequalification";

export const prequalificationApi = {
  list: (query?: PrequalificationListQuery) =>
    api.get<PagedResult<PrequalificationSummaryDto>>(BASE, query),
  /** Stat-card counters — same filters as `list` except `status`; archived
   *  always excluded server-side. */
  stats: (query?: PrequalificationListQuery) =>
    api.get<PrequalificationStatsDto>(`${BASE}/stats`, query),
  get: (id: string) => api.get<PrequalificationDetailDto>(`${BASE}/${id}`),
  /** The submission's answers in plain English, already formatted and already
   *  explained server-side.
   *
   *  A SIBLING of `get`, not part of it: the drill-in drawer wants only these
   *  rows, so it fetches only these rows, when it opens. The full-page detail
   *  payload is unchanged and its consumers are untouched.
   *
   *  Same `Prequalification.Read` gate as `get`, and nothing newly disclosed —
   *  every input is a column `get` already returns verbatim to the same
   *  caller. */
  answers: (id: string) =>
    api.get<PrequalAnswerDisplayItem[]>(`${BASE}/${id}/answers`),
  activity: (id: string) =>
    api.get<PrequalificationActivityDto[]>(`${BASE}/${id}/activity`),
  /** Assign (or clear, with null) the staff owner. */
  assign: (id: string, userId: string | null) =>
    api.post<void>(`${BASE}/${id}/assign`, { userId }),
  changeStatus: (id: string, status: PrequalificationStatus, reason?: string) =>
    api.post<void>(`${BASE}/${id}/status`, { status, reason }),
  /** Record whether staff have checked an internal buyer's asserted
   *  relationship. Refused by the server on a submission that asserted none, and
   *  deliberately NOT idempotent — re-asserting the same state is a real event
   *  ("somebody looked again today"), and it writes a fresh activity row and a
   *  fresh timestamp. */
  setInternalVerification: (id: string, state: PrequalInternalVerification) =>
    api.post<void>(`${BASE}/${id}/internal-verification`, { state }),
  archive: (id: string) => api.post<void>(`${BASE}/${id}/archive`),
  unarchive: (id: string) => api.post<void>(`${BASE}/${id}/unarchive`),
  /** Convert to a Party — link an existing one or create a new one from the
   *  submission's contact fields. Returns the PARTY id; the caller derives the
   *  registry to redirect into from the submission's own role. */
  convert: (id: string, body?: ConvertPrequalificationPayload) =>
    api.post<string>(`${BASE}/${id}/convert`, body ?? {}),
  /** What the "Send to the assignment queue" dialog shows: eligibility, the
   *  linked buyer, contact checks and duplicate matches. */
  buyerCrm: (id: string) =>
    api.get<PrequalificationBuyerCrmStateDto>(`${BASE}/${id}/buyer-crm`),
  /** Send to the Buyer CRM assignment queue, or link to an existing buyer. A
   *  409 carrying `prequalAlreadyInBuyerCrm` means somebody got there first —
   *  read it with `alreadyInBuyerCrm`. */
  sendToBuyerCrm: (id: string, body?: SendPrequalificationToBuyerCrmPayload) =>
    api.post<PrequalificationBuyerCrmResultDto>(`${BASE}/${id}/send-to-buyer-crm`, body ?? {}),
  /** Fresh short-lived read link for a generated document. */
  /** Re-render this submission's documents. Append-only: the existing ones
   *  survive and are marked superseded. Gated on `Prequalification.Read` (D9),
   *  deliberately wider than the module's other writes, so that everyone who
   *  can SEE a broken record can fix it. */
  regenerateDocuments: (id: string) =>
    api.post<PrequalificationRegenerationResultDto>(`${BASE}/${id}/documents/regenerate`),

  /** What a resend would send. Nothing is sent and nothing is recorded. */
  resendDraft: (id: string) =>
    api.get<PrequalificationResendDraftDto>(`${BASE}/${id}/resend`),

  /** Send the applicant their documents again. The attachments are the STORED
   *  documents — this re-renders nothing; Regenerate is the other control. A
   *  blank subject or body keeps the composed one. */
  resend: (id: string, body?: { subject?: string; body?: string }) =>
    api.post<PrequalificationResendResultDto>(`${BASE}/${id}/resend`, body ?? {}),

  documentLink: (id: string, docId: string) =>
    api.get<PrequalificationDocumentLinkDto>(`${BASE}/${id}/documents/${docId}/link`),

  /** An editable Word (.docx) copy of a generated document, converted from the
   *  exact HTML its PDF was rendered from. 404 when `hasWordCopy` is false. */
  documentWord: (id: string, docId: string) =>
    api.getBlob(`${BASE}/${id}/documents/${docId}/word`),

  // Invites — reads ride Prequalification.Read, writes Prequalification.Manage.
  listInvites: (query?: PrequalInviteListQuery) =>
    api.get<PagedResult<PrequalInviteDto>>(`${BASE}/invites`, query),
  sendInvite: (body: SendPrequalInvitePayload) =>
    api.post<PrequalInviteDto>(`${BASE}/invites`, body),
  /** Re-mail an outstanding or expired invite; the link is not reminted. */
  resendInvite: (id: string) =>
    api.post<PrequalInviteDto>(`${BASE}/invites/${id}/resend`),
  revokeInvite: (id: string) => api.post<void>(`${BASE}/invites/${id}/revoke`),

  /** In-progress drafts. Reads ride Prequalification.Read — the permission that
   *  already gates this surface; no new one was minted. */
  listDrafts: (query?: PrequalificationDraftListQuery) =>
    api.get<PagedResult<PrequalificationDraftSummaryDto>>(`${BASE}/drafts`, query),

  // ── Assisted completion (Prequalification.Manage) ─────────────────────────
  // The same permission that already gates sending an invite and starting an
  // in-office draft on this surface. No new permission was minted: an ungranted
  // one is ADMIN-only by default, and the broker liaison would silently lose the
  // feature.

  /** Start an in-office prequalification: mints a suppressed invite so the
   *  draft is INVITED rather than organic, and returns the draft to drive. No
   *  email is sent — the client is in the room. */
  startInOfficeDraft: (body: SendPrequalInvitePayload) =>
    api.post<PrequalificationInOfficeStartDto>(`${BASE}/drafts`, body),

  assistLoad: (draftId: string) =>
    api.get<PrequalificationDraftAssistedLoadDto>(`${BASE}/drafts/${draftId}/assist`),

  assistStep: (draftId: string, body: PrequalificationAssistedStepBody) =>
    api.put<PrequalificationDraftPersistResult>(
      `${BASE}/drafts/${draftId}/assist/step`, body),

  assistFinalize: (draftId: string, body: PrequalificationAssistedFinalizeBody) =>
    api.post<PrequalFinalizeResult>(`${BASE}/drafts/${draftId}/assist/finalize`, body),
};
